import { useFormContext } from "react-hook-form";
import { Button, Spinner } from "flowbite-react";
import { ChevronLeft } from "lucide-react";
import type { ModalSteps } from "@/features/card/components/OnboardingModal";
import { CardActivationFormData } from "@/features/card/schemas/forms/cardActivationForm.schema";
import { cn } from "@/shared/utils/style.util";

type ButtonConfig = {
  text: string
  onClick: () => void
  isIconButton?: boolean
  disabled?: boolean
  dataTestId?: string
};

type ModalStateConfig = {
  leftButton?: ButtonConfig
  rightButton: ButtonConfig
};

type OnboardingModalFooterProps = {
  modalStep: ModalSteps
  isCardFlipped: boolean
  isUpdating: boolean
  onClose: () => void
  onBackToWelcome: () => void
  onFlipCard: () => void
  onActivateCard: () => void
  onUseCard: () => void
  onSetModalStep: (state: ModalSteps) => void
};

export const OnboardingModalFooter = ({
  modalStep,
  isCardFlipped,
  isUpdating,
  onClose,
  onBackToWelcome,
  onFlipCard,
  onActivateCard,
  onUseCard,
  onSetModalStep,
}: OnboardingModalFooterProps) => {
  const { watch, formState: { errors, isSubmitting } } = useFormContext<CardActivationFormData>();

  const isCardActionCodeValid = watch("public_token")?.length === 9 && !errors.public_token;

  const getModalStateConfig = (): ModalStateConfig => {
    const stateConfigs: Record<ModalSteps, ModalStateConfig> = {
      "new-card": {
        leftButton: {
          text: "Plus tard",
          onClick: onClose,
          dataTestId: "later-button",
        },
        rightButton: {
          text: "Commencer l'activation",
          onClick: () => onSetModalStep("card-activation"),
          dataTestId: "start-activation-button",
        },
      },
      "card-activation": {
        leftButton: {
          text: "Retour",
          onClick: onBackToWelcome,
          isIconButton: true,
          dataTestId: "back-button",
        },
        rightButton: {
          text: !isCardFlipped ? "Retourner votre carte" : (isUpdating ? "Activation en cours..." : "Activer ma carte"),
          onClick: !isCardFlipped ? onFlipCard : onActivateCard,
          disabled: isCardFlipped && (!isCardActionCodeValid || isSubmitting || isUpdating),
          dataTestId: !isCardFlipped ? "flip-card-button" : "activate-card-button",
        },
      },
      "card-success": {
        rightButton: {
          text: "Utiliser ma carte",
          onClick: onUseCard,
          dataTestId: "use-card-button",
        },
      },
    };

    return stateConfigs[modalStep];
  };

  const config = getModalStateConfig();

  return (
    <>
      {config.leftButton && (
        <Button
          type="button"
          onClick={config.leftButton.onClick}
          color="lightprimary"
          className={cn(
            "gap-1 whitespace-nowrap overflow-hidden [grid-area:left]",
            config.leftButton.isIconButton ? "w-12 p-0" : "w-full",
            "transition-[width] duration-300 ease-in-out",
          )}
          disabled={config.leftButton.disabled ?? false}
          data-testid={config.leftButton.dataTestId}
        >
          <ChevronLeft aria-label={config.leftButton.text} size="24" />
          <span className={cn(
            "transition-opacity duration-200 delay-150 ease-in-out",
            config.leftButton.isIconButton ? "opacity-0" : "opacity-100",
          )}
          >
            {config.leftButton.isIconButton ? "" : config.leftButton.text}
          </span>
        </Button>
      )}

      <Button
        type="button"
        onClick={config.rightButton.onClick}
        color="primary"
        className="w-full flex gap-3 transition-all duration-300 ease-in-out [grid-area:right]"
        disabled={config.rightButton.disabled}
        data-testid={config.rightButton.dataTestId}
      >
        {isSubmitting && <Spinner size="sm" />}
        <span className="line-clamp-1">{config.rightButton.text}</span>
      </Button>
    </>
  );
};
